import React, { createContext, useContext, useState, useEffect } from 'react';

interface APIKeyContextType {
  apiKey: string | null;
  setApiKey: (key: string) => void;
  clearApiKey: () => void;
  validateKey: (key: string) => boolean;
}

const APIKeyContext = createContext<APIKeyContextType>({
  apiKey: null,
  setApiKey: () => {},
  clearApiKey: () => {},
  validateKey: () => false,
});

export const useAPIKey = () => useContext(APIKeyContext);

export const APIKeyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [apiKey, setApiKeyState] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const storedKey = localStorage.getItem('openai_api_key');
    if (storedKey) {
      setApiKeyState(storedKey);
    }
    setInitialized(true);
  }, []);

  const setApiKey = (key: string) => {
    if (!validateKey(key)) return;
    localStorage.setItem('openai_api_key', key);
    setApiKeyState(key);
  };

  const clearApiKey = () => {
    localStorage.removeItem('openai_api_key');
    setApiKeyState(null);
  };

  const validateKey = (key: string): boolean => {
    return key.startsWith('sk-') && key.length >= 40;
  };

  if (!initialized) {
    return null; // Don't render children until we've checked localStorage
  }

  return (
    <APIKeyContext.Provider value={{ apiKey, setApiKey, clearApiKey, validateKey }}>
      {children}
    </APIKeyContext.Provider>
  );
};